/* --- js/critical/nodelist-ext.js --- */
/**
 * Calls a defined callback function on each element of a NodeList, and returns an array that contains the results.
 * @template {Node} T
 * @template U
 * @this {NodeListOf<T>}
 * @param {function(T, number, NodeListOf<T>): U} callback - A function that accepts up to three arguments.
 * @param {*} [thisArg] - An object to which the this keyword can refer in the callbackfn function.
 * @returns {U[]}
 */
NodeList.prototype.map = function (callback, thisArg) {
  const result = /** @type {U[]} */ ([]);
  this.forEach((...args) => result.push(callback.apply(thisArg, args)));
  return result;
};

/**
 * Returns the elements of a NodeList that meet the condition specified in a callback function.
 * @template {Node} T
 * @this {NodeListOf<T>}
 * @param {function(T, number, NodeListOf<T>): unknown} predicate - A function that accepts up to three arguments.
 * @param {*} [thisArg] - An object to which the this keyword can refer in the predicate function.
 * @returns {T[]}
 */
NodeList.prototype.filter = function (predicate, thisArg) {
  const result = /** @type {T[]} */ ([]);
  this.forEach((item, index, list) => {
    if (predicate.call(thisArg, item, index, list)) {
      result.push(item);
    }
  });
  return result;
};

/**
 * Calls the specified callback function for all the elements in a NodeList.
 * @template {Node} T
 * @template U
 * @this {NodeListOf<T>}
 * @param {function(U, T, number, NodeListOf<T>): U} callback - A function that accepts up to four arguments.
 * @param {U} initialValue - The initial value to start the accumulation.
 * @returns {U}
 */
NodeList.prototype.reduce = function (callback, initialValue) {
  let accumulator = initialValue;
  this.forEach((item, index, list) => {
    accumulator = callback(accumulator, item, index, list);
  });
  return accumulator;
};


/* --- js/critical/mini-alpine.js --- */
/**
 * @fileoverview mini-apline.js — A lightweight signals-based reactivity system
 * with declarative DOM directives, designed for server-rendered HTML
 * (Shopify Liquid, htmx-style environments).
 *
 * Directives (all use `mx-` prefix, namespaced away from plain `data-*`):
 *   mx-scope="{ count: 0 }"   — creates a reactive scope
 *   mx-text="expr"            — sets el.textContent reactively
 *   mx-effect="stmt"          — runs a statement reactively (no DOM event needed)
 *   mx-bind:attr="expr"       — sets any attribute reactively
 *   mx-class="expr"           — adds/removes classes (expr → string or object)
 *   mx-on:event="stmt"        — event listener
 *   mx-ref="name"             — registers el on the scope as scope.$refs.name
 *   mx-model="prop"           — two-way binding for inputs
 *   mx-for="(item, index) in expr" — clones a <template> per list item
 *   mx-show="isOpen"          - toggles a two-phase visibility sequence using data attributes
 *
 */

// ---------------------------------------------------------------------------
// Type Definitions
// ---------------------------------------------------------------------------

/** @typedef {import("../types").ReactiveScope} ReactiveScope */

/** Symbol used to mark already-initialized elements */
const _INITIALIZED = Symbol("reactive.initialized");

/** Symbol used to store dispose functions on elements */
const _DISPOSE = Symbol("reactive.dispose");

/** Directive used to check if an element is part of alpine */
const DIRECTIVE_REGEX =
  /^mx-(scope|text|effect|class|model|ref|for|show|bind:|on:)/;

/** Matches "item in expr" or "(item, index) in expr" for mx-for */
const FOR_EXPR_REGEX = /^\(?\s*([\w$]+)\s*(?:,\s*([\w$]+)\s*)?\)?\s+in\s+(.+)$/;

/** @typedef {import("./mini-htmx").MiniHTMX} MiniHTMX */

// ---------------------------------------------------------------------------
// Reactivity core
// ---------------------------------------------------------------------------

class MiniAlpine {
  /** @type {MiniHTMX} */
  #htmx;

  /**
   * @param {MiniHTMX} htmx
   */
  constructor(htmx) {
    this.#htmx = htmx;
  }

  // ---------------------------------------------------------------------------
  // Scope — reactive proxy over a plain object
  // ---------------------------------------------------------------------------

  /**
   * Wraps a plain data object so that every property access goes through a
   * signal, making the object transparently reactive.
   *
   * @param {Record<string | symbol, *>} rawData
   * @param {ReactiveScope} [parentScope] inherited scope for nested lookups
   * @returns {ReactiveScope} Reactive proxy
   */
  #makeScope(rawData, parentScope) {
    /** @type {Record<string | symbol, Signal<any>>} */
    const _signals = {};

    // Seed a signal for each initial property
    for (const [key, val] of Object.entries(rawData)) {
      _signals[key] = signal(val);
    }

    // $refs storage for mx-ref directives
    const $refs = Object.create(parentScope ? parentScope.$refs : null);
    const $htmx = this.#htmx;

    const scope = new Proxy(rawData, {
      get(_, key) {
        if (key === "$refs") return $refs;
        if (key === "$htmx") return $htmx;
        if (_signals[key]) return _signals[key].value;
        // Fall through to parent scope for inherited state
        if (parentScope && key in parentScope) return parentScope[key];
        return rawData[key];
      },
      set: (_, key, value) => {
        if (_signals[key]) {
          _signals[key].value = value;
        } else if (parentScope && key in parentScope) {
          // Pass the mutation up the chain instead of swallowing it locally
          parentScope[key] = value;
        } else {
          _signals[key] = signal(value);
        }
        return true;
      },
      has(_, key) {
        if (key === Symbol.unscopables) return false;
        if (key === "$refs") return true;
        if (key === "$htmx") return true;
        return key in _signals || (parentScope ? key in parentScope : false);
      },
      ownKeys() {
        return Object.keys(_signals);
      },
    });

    return /** @type {ReactiveScope} */ (scope);
  }

  // ---------------------------------------------------------------------------
  // Expression evaluation
  // ---------------------------------------------------------------------------

  /** @type {Map<string, function>} */
  #exprCache = new Map();

  /**
   * Evaluates a JS expression string against a reactive scope.
   * Scope properties are available as bare names (no `scope.` prefix).
   *
   * @param {string} expr
   * @param {Record<string, *>} scope
   * @returns {*}
   */
  #evaluate(expr, scope) {
    const cacheKey = "expr:" + expr;

    if (!this.#exprCache.has(cacheKey)) {
      try {
        const fn = new Function("$scope", `with($scope){ return (${expr}) }`);
        this.#exprCache.set(cacheKey, fn);
      } catch (e) {
        throw Error(
          `[mini-alpine]: can't evaluate expression: '${expr}', got ${e}`,
        );
      }
    }

    const fn = this.#exprCache.get(cacheKey);
    if (!fn) {
      throw Error(`[mini-alpine]: can't evaluate expression: ${expr}`);
    }
    return fn(scope);
  }

  /**
   * Evaluates a JS statement (no return value) against a scope.
   * Used for mx-on:event handlers and mx-effect.
   *
   * @param {string} stmt
   * @param {Record<string, *>} scope
   * @param {Event} [event] the DOM event, available as `$event` in the expression
   */
  #execute(stmt, scope, event) {
    const cacheKey = "stmt:" + stmt;

    if (!this.#exprCache.has(cacheKey)) {
      try {
        this.#exprCache.set(
          cacheKey,
          new Function("$scope", "$event", `with($scope){${stmt}}`),
        );
      } catch (e) {
        throw Error(
          `[mini-alpine]: can't compile statement: '${stmt}', got ${e}`,
        );
      }
    }

    const fn = this.#exprCache.get(cacheKey);
    if (!fn) {
      throw Error(`[mini-alpine]: can't find expression for key: ${cacheKey}`);
    }

    try {
      fn(scope, event);
    } catch (e) {
      throw Error(
        `[mini-alpine]: can't execute statement: '${stmt}', got ${e}`,
      );
    }
  }

  // ---------------------------------------------------------------------------
  // Directive bindings
  // ---------------------------------------------------------------------------

  /**
   * Binds `mx-text` — sets el.textContent to the evaluated expression.
   *
   * @param {HTMLElement} el
   * @param {string} expr
   * @param {ReactiveScope} scope
   * @returns {function(): void} dispose
   */
  #bindText(el, expr, scope) {
    return effect(() => {
      el.textContent = String(this.#evaluate(expr, scope) ?? "");
    });
  }

  /**
   * Binds `mx-effect` — runs a statement reactively whenever any signal
   * it reads changes, regardless of how those signals changed.
   *
   * @param {HTMLElement} _el
   * @param {string} stmt
   * @param {ReactiveScope} scope
   * @returns {function(): void} dispose
   */
  #bindEffect(_el, stmt, scope) {
    return effect(() => {
      this.#execute(stmt, scope);
    });
  }

  /**
   * Binds `mx-bind:attrName` — sets an attribute reactively.
   * If attrName is `value`, sets the property directly (for inputs).
   *
   * @param {HTMLElement} el
   * @param {string} attr
   * @param {string} expr
   * @param {ReactiveScope} scope
   * @returns {function(): void} dispose
   */
  #bindAttr(el, attr, expr, scope) {
    // Attributes whose live state is a DOM property, not the attribute
    const PROP_ATTRS = new Set([
      "checked",
      "value",
      "selected",
      "disabled",
      "indeterminate",
    ]);

    // Loose view of the element for dynamic property access
    const target = /** @type {Record<string, any>} */ (el);

    return effect(() => {
      const val = this.#evaluate(expr, scope);

      if (attr in target && PROP_ATTRS.has(attr)) {
        if (typeof target[attr] === "boolean") {
          target[attr] = Boolean(val);
        } else {
          target[attr] = val ?? "";
        }
      }

      // Keep the attribute in sync too (for CSS selectors, SSR parity, etc.)
      if (val === false || val === null || val === undefined) {
        el.removeAttribute(attr);
      } else if (val === true) {
        el.setAttribute(attr, "");
      } else {
        el.setAttribute(attr, String(val));
      }
    });
  }

  /**
   * Binds `mx-class` — reactively adds/removes classes.
   *
   * @param {HTMLElement} el
   * @param {string} expr
   * @param {ReactiveScope} scope
   * @returns {function(): void} dispose
   */
  #bindClass(el, expr, scope) {
    /** @type {string[]} */
    let _prevClasses = [];

    return effect(() => {
      const val = this.#evaluate(expr, scope);
      /** @type {string[]} */
      let nextClasses = [];

      if (typeof val === "string") {
        nextClasses = val.split(/\s+/).filter(Boolean);
      } else if (val && typeof val === "object") {
        nextClasses = Object.entries(val)
          .filter(([, active]) => active)
          .map(([cls]) => cls);
      }

      // Remove stale, add new
      for (const cls of _prevClasses) {
        if (!nextClasses.includes(cls)) el.classList.remove(cls);
      }
      for (const cls of nextClasses) {
        if (!_prevClasses.includes(cls)) el.classList.add(cls);
      }

      _prevClasses = nextClasses;
    });
  }

  /**
   * Binds `mx-model` — two-way binding between an input/select/textarea
   * and a scope property.
   *
   * @param {HTMLElement} el
   * @param {string} prop — property name on scope (not an expression)
   * @param {ReactiveScope} scope
   * @returns {function(): void} dispose
   */
  #bindModel(el, prop, scope) {
    const input = /** @type {HTMLInputElement} */ (el);
    const isCheckbox = input.type === "checkbox";
    const isNumber = input.type === "number" || input.type === "range";

    // Scope → DOM
    const disposeEffect = effect(() => {
      const val = scope[prop];
      if (isCheckbox) {
        input.checked = Boolean(val);
      } else {
        input.value = String(val ?? "");
      }
    });

    // DOM → Scope
    const handler = () => {
      if (isCheckbox) {
        scope[prop] = input.checked;
      } else if (isNumber) {
        scope[prop] = input.valueAsNumber;
      } else {
        scope[prop] = input.value;
      }
    };

    const eventName =
      input.tagName === "SELECT" || isCheckbox ? "change" : "input";
    input.addEventListener(eventName, handler);

    return () => {
      disposeEffect();
      input.removeEventListener(eventName, handler);
    };
  }

  /**
   * Binds `mx-on:eventName` — attaches an event listener that executes
   * the expression against the scope.
   *
   * @param {HTMLElement} el
   * @param {string} eventWithModifiers — e.g. "click.prevent.stop"
   * @param {string} stmt
   * @param {ReactiveScope} scope
   * @returns {function(): void} dispose
   */
  #bindEvent(el, eventWithModifiers, stmt, scope) {
    const [eventName, ...modifiers] = eventWithModifiers.split(".");

    const options = /** @type {AddEventListenerOptions} */ ({
      once: modifiers.includes("once"),
    });

    const target = modifiers.includes("window") ? window : el;

    const handler = /** @type {EventListener} */ (event) => {
      if (modifiers.includes("prevent")) event.preventDefault();
      if (modifiers.includes("stop")) event.stopPropagation();
      this.#execute(stmt, scope, event);
    };

    target.addEventListener(eventName, handler, options);

    return () => target.removeEventListener(eventName, handler, options);
  }

  /**
   * Binds `mx-for` — clones the contents of a `<template>` element once per
   * item in a list.
   *
   * @param {HTMLTemplateElement} templateEl
   * @param {string} expr
   * @param {ReactiveScope} scope
   * @returns {function(): void} dispose
   */
  #bindFor(templateEl, expr, scope) {
    if (templateEl.tagName !== "TEMPLATE") {
      throw Error(
        `[mini-alpine]: mx-for must be used on a <template> element, got <${templateEl.tagName.toLowerCase()}>`,
      );
    }

    const match = expr.match(FOR_EXPR_REGEX);
    if (!match) {
      throw Error(`[mini-alpine]: invalid mx-for expression: '${expr}'`);
    }
    const [, itemName, indexName, listExpr] = match;

    const parent = templateEl.parentNode;
    if (!parent) {
      throw Error("[mini-alpine]: mx-for template must have a parent element");
    }

    const anchor = document.createComment(`mx-for: ${expr}`);
    parent.insertBefore(anchor, templateEl);
    templateEl.remove();

    /** @type {{ dispose: () => void, nodes: ChildNode[] }[]} */
    let rendered = [];

    const disposeEffect = effect(() => {
      const list = this.#evaluate(listExpr, scope);
      const items = Array.isArray(list) ? list : [];

      for (const entry of rendered) {
        entry.dispose();
        entry.nodes.forEach((n) => n.remove());
      }
      rendered = [];

      let insertAfter = /** @type {ChildNode} */ (anchor);
      items.forEach((item, index) => {
        const rawData = { [itemName]: item };
        if (indexName) rawData[indexName] = index;
        const childScope = this.#makeScope(rawData, scope);

        const fragment = /** @type {DocumentFragment} */ (
          templateEl.content.cloneNode(true)
        );
        const nodes = Array.from(fragment.childNodes);

        /** @type {(() => void)[]} */
        const disposes = [];

        for (const node of nodes) {
          parent.insertBefore(node, insertAfter.nextSibling);
          insertAfter = node;
          if (node.nodeType === Node.ELEMENT_NODE) {
            const elNode = /** @type {HTMLElement} */ (node);
            elNode._reactiveScope = childScope;
            this.init(elNode);
            disposes.push(() => this.dispose(elNode));
          }
        }

        rendered.push({
          dispose: () => disposes.forEach((d) => d()),
          nodes,
        });
      });
    });

    return () => {
      disposeEffect();
      for (const entry of rendered) {
        entry.dispose();
        entry.nodes.forEach((n) => n.remove());
      }
      anchor.remove();
    };
  }

  /**
   * Binds `mx-show` — toggles a two-phase visibility sequence using
   * data attributes, leaving all display/transition styling to CSS.
   *
   * @param {HTMLElement} el
   * @param {string} expr
   * @param {ReactiveScope} scope
   * @returns {function(): void} dispose
   */
  #bindShow(el, expr, scope) {
    let first = true;
    let pendingCleanup = /** @type { function | null } */ (null);

    const clearPending = () => {
      if (pendingCleanup) {
        pendingCleanup();
        pendingCleanup = null;
      }
    };

    const disposeEffect = effect(() => {
      const show = Boolean(this.#evaluate(expr, scope));
      clearPending();

      if (first) {
        el.toggleAttribute("data-show", show);
        el.toggleAttribute("data-appear", show);
        first = false;
        return;
      }

      if (show) {
        el.toggleAttribute("data-show", true);
        // For Firefox, rAF ensure flushing last transition.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => el.toggleAttribute("data-appear", true));
        });
      } else {
        el.toggleAttribute("data-appear", false);

        const finish = () => {
          el.toggleAttribute("data-show", false);
          pendingCleanup = null;
        };
        const handler = /** @type {EventListener} */ (event) => {
          if (event.target !== el) return;
          finish();
        };
        el.addEventListener("transitionend", handler, { once: true });

        pendingCleanup = () => {
          el.removeEventListener("transitionend", handler);
        };
      }
    });

    return () => {
      clearPending();
      disposeEffect();
    };
  }

  // ---------------------------------------------------------------------------
  // Initialization
  // ---------------------------------------------------------------------------

  /**
   * Finds the nearest ancestor scope for an element.
   *
   * @param {HTMLElement} el
   * @returns {ReactiveScope | undefined}
   */
  #findParentScope(el) {
    let node = el.parentElement;
    while (node) {
      if (node._reactiveScope) return node._reactiveScope;
      node = node.parentElement;
    }
    return undefined;
  }

  /**
   * Initializes all directives on a single element, given a scope.
   * Returns a dispose function that cleans up all bindings.
   *
   * @param {HTMLElement} el
   * @param {ReactiveScope} scope
   * @returns {function(): void} dispose
   */
  #initElement(el, scope) {
    /** @type {(() => void)[]} */
    const disposes = [];

    for (const attr of [...el.attributes]) {
      const name = attr.name;
      const value = attr.value;

      switch (true) {
        case name === "mx-text":
          disposes.push(this.#bindText(el, value, scope));
          break;

        case name === "mx-effect":
          disposes.push(this.#bindEffect(el, value, scope));
          break;

        case name === "mx-class":
          disposes.push(this.#bindClass(el, value, scope));
          break;

        case name === "mx-model":
          disposes.push(this.#bindModel(el, value, scope));
          break;

        case name === "mx-for":
          disposes.push(
            this.#bindFor(
              /** @type {HTMLTemplateElement} */ (el),
              value,
              scope,
            ),
          );
          break;

        case name === "mx-show":
          disposes.push(this.#bindShow(el, value, scope));
          break;

        case name.startsWith("mx-bind:"):
          const attrName = name.slice("mx-bind:".length);
          disposes.push(this.#bindAttr(el, attrName, value, scope));
          break;

        case name.startsWith("mx-on:"):
          const eventPart = name.slice("mx-on:".length);
          disposes.push(this.#bindEvent(el, eventPart, value, scope));
          break;

        case name === "mx-ref":
          scope.$refs[value] = el;
          break;
      }
    }

    return () => disposes.forEach((d) => d());
  }

  /**
   * Walks the DOM tree rooted at `root`, finds all `mx-scope` elements,
   * creates reactive scopes, and binds all child directives.
   *
   * @param {HTMLElement | Document} [root=document]
   */
  init(root = document) {
    const elements =
      root === document
        ? document.querySelectorAll("*")
        : [root, ...root.querySelectorAll("*")];

    for (const e of elements) {
      const element = /** @type HTMLElement */ (e);
      if (element[_INITIALIZED]) continue;

      const hasDirectives =
        element.hasAttributes() &&
        Array.from(element.attributes).some((attr) =>
          DIRECTIVE_REGEX.test(attr.name),
        );
      if (!hasDirectives) continue;

      if (element.hasAttribute("mx-scope")) {
        const parentScope = this.#findParentScope(element);

        let rawData;
        const eleAttribute = element.getAttribute("mx-scope");
        if (parentScope) {
          try {
            const evaluator = new Function(
              "$parent",
              `with($parent) { return (${eleAttribute}) }`,
            );
            rawData = evaluator(parentScope);
          } catch (e) {
            throw Error(
              `[mini-alpine]: can't compile scope: '${eleAttribute}', got ${e}`,
            );
          }
        } else {
          try {
            rawData = new Function(`return (${eleAttribute})`)();
          } catch (e) {
            throw Error(
              `[mini-alpine]: can't compile scope: '${eleAttribute}', got ${e}`,
            );
          }
        }

        element._reactiveScope = this.#makeScope(rawData, parentScope);
      }

      const scope = element._reactiveScope || this.#findParentScope(element);

      if (!scope) {
        console.warn(
          "[mini-alpine]: Directives found but no parent scope exists.",
          element,
        );
        continue;
      }

      element[_INITIALIZED] = true;
      element[_DISPOSE] = this.#initElement(element, scope);
    }
  }

  /**
   * Disposes all reactive bindings on elements within `root`.
   *
   * @param {HTMLElement} root
   */
  dispose(root) {
    let elements = /** @type HTMLElement[] */ (
      Array.from(root.querySelectorAll("*"))
    );
    if (root[_DISPOSE]) {
      elements.push(root);
    }
    for (const e of elements) {
      if (e[_DISPOSE]) {
        e[_DISPOSE]();
        delete e[_DISPOSE];
        delete e[_INITIALIZED];
      }
    }
  }
}


/* --- js/critical/mini-htmx.js --- */
// ---------------------------------------------------------------------------
// Tags and Events
// ---------------------------------------------------------------------------

const _HXEVENTBEFORESWAP = "hx:before-swap";
const _HXEVENTAFTERSWAP = "hx:after-swap";
const _HXEVENTINIT = "hx:init";

const _HXGET = "hx-get";
const _HXGETPAGE = "hx-get-page";
const _HXPOST = "hx-post";
const _HXTRIGGER = "hx-trigger";
const _HXSWAP = "hx-swap";
const _HXREPLACE = "hx-replace";
const _HXSECTIONID = "hx-section-id";
const _HXGROUPID = "hx-group-id";
const _HXANNOUNCE = "hx-announce";

// ---------------------------------------------------------------------------
// Type Definitions
// ---------------------------------------------------------------------------

/**
 * @typedef {'innerHTML' | 'outerHTML'} SwapStyle
 *
 * Used to enforce type checking.
 * @typedef {string & { __brand: 'SectionId' }} SectionId
 * @typedef {string & { __brand: 'GroupId' }} GroupId
 * @typedef {string & { __brand: 'ElementId' }} ElementId
 *
 * @typedef {import("../types").ElementMeta} ElementMeta
 *
 * @typedef {Object} RequestOptions
 * @property {string} url - The URL to fetch
 * @property {'GET' | 'POST'} method - HTTP method
 * @property {Element} target - The primary target element to swap content into
 * @property {Element} triggerEl - The element that triggered the request (for loading state)
 *
 * @typedef {import("./mini-alpine").MiniAlpine} MiniAlpine
 * import { signal } from "./signals.js";
 */

// ---------------------------------------------------------------------------
// Mini HTMX
// ---------------------------------------------------------------------------

class MiniHTMX {
  #initSelector = `[${_HXGET}],[${_HXPOST}],[${_HXGETPAGE}]`;
  /** @type {MutationObserver | null} */
  #observer = null;
  /** @type {MiniAlpine | null} */
  #miniAlpine = null;
  #loading;
  #preloading;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #debounceTimer = null;
  /** @type {{ element: Element; ms: number } | null} */
  #pendingDebounce = null;

  /**
   * Triggers an HTMX request on the given element.
   * @param {Element} element - The element to trigger the request on
   */
  trigger(element) {
    return this.#delegateStrategy(element);
  }

  /**
   * Schedules a request, always using the most recently passed element.
   * If a request is currently in flight, waits for it to resolve before
   * starting the debounce window — so freshly-typed input never fires
   * until the previous request has fully settled.
   * @param {Element} element
   * @param {number} ms
   */
  debouncedTrigger(element, ms) {
    if (this.#debounceTimer !== null) clearTimeout(this.#debounceTimer);

    if (this.#loading.value) {
      const alreadyWaiting = this.#pendingDebounce !== null;
      this.#pendingDebounce = { element, ms };
      if (!alreadyWaiting) this.#waitForLoadingToClear();
      return;
    }

    this.#preloading.value = true;
    this.#debounceTimer = setTimeout(() => {
      if (this.#loading.value) {
        this.debouncedTrigger(element, ms);
        return;
      }
      this.#preloading.value = false;
      this.#delegateStrategy(element);
    }, ms);
  }

  /**
   * Resolves the next time #loading becomes false. If it's already false,
   * resolves on the next microtask (avoids synchronously resolving inside
   * the effect's own tracking pass).
   */
  #waitForLoadingToClear() {
    const dispose = effect(() => {
      if (!this.#loading.value) {
        if (this.#pendingDebounce !== null) {
          const { element, ms } = this.#pendingDebounce;
          this.debouncedTrigger(element, ms);
          this.#pendingDebounce = null;
        } else {
          throw new Error("cannot wait for pendingDebounce, it is null");
        }
        dispose();
      }
    });
  }

  /**
   * Public interface for #process.
   * @param {Element} [root=document.body] = The root element to scan for HTMX-like attributes
   */
  process(root = document.body) {
    return this.#process(root);
  }

  get loading() {
    return this.#loading.value;
  }

  get preloading() {
    return this.#preloading.value;
  }

  /**
   * Processes a root element and initialises all child elements with data-get or data-post attributes.
   * Skips elements that have already been initialised (identified by _myHtmx metadata).
   * @param {Element} [root=document.body] - The root element to scan for HTMX-like attributes
   */
  #process(root = document.body) {
    const selector = `[${_HXGET}],[${_HXPOST}],[${_HXGETPAGE}],[${_HXANNOUNCE}]`;

    root.querySelectorAll(selector).forEach((element) => {
      if (element._miniHtmx) return;
      if (element.matches(this.#initSelector)) this.#initElement(element);
      element.dispatchEvent(new Event(_HXEVENTINIT, { bubbles: true }));
    });
  }

  /**
   * Initialises an element by attaching an event listener based on its data-trigger attribute.
   * @param {Element} element - The element to initialise
   */
  #initElement(element) {
    /** @type {{ trigger: string, handler: EventListener }[]} */
    const listeners = [];
    /** @type string[]} */
    let triggers;
    const raw = element.getAttribute(_HXTRIGGER) || "";

    if (raw.trim() === "manual") {
      triggers = [];
    } else if (raw.trim() === "") {
      triggers = [this.#getDefaultTrigger(element)];
    } else {
      triggers = raw.split(",").map((part) => part.trim());
    }

    triggers.forEach((trigger) => {
      /** @type {EventListener} */
      const handler = (event) => {
        event?.preventDefault();
        this.#delegateStrategy(element);
      };
      element.addEventListener(trigger, handler);
      listeners.push({ trigger, handler });
    });

    element._miniHtmx = { listeners };
  }

  /**
   * Removes event listeners from all initialised elements within a root,
   * and deletes their _myHtmx metadata. Call before swapping out DOM content.
   * @param {Element} root - The root element whose descendants should be cleaned up
   */
  #cleanup(root) {
    root.querySelectorAll(this.#initSelector).forEach((element) => {
      if (element._miniHtmx) {
        element._miniHtmx.listeners.forEach(({ trigger, handler }) => {
          element.removeEventListener(trigger, handler);
        });
        delete element._miniHtmx;
      }
    });
  }

  /**
   * Swaps content into a target element using the specified swap style.
   * After swapping, cleans up old listeners and re-processes the new content.
   * For outerHTML swaps, the target is replaced entirely so the parent is re-processed.
   * @param {ElementId} elementId - The element to swap content into
   * @param {Document} newDoc - The parsed HTML document to search for the same Id
   */
  #swapContent(elementId, newDoc) {
    const prev = document.getElementById(elementId);
    if (!prev) {
      // Element not found, skip swap.
      return;
    }

    this.#cleanup(prev);
    this.#miniAlpine?.dispose(prev);

    const next = newDoc.getElementById(elementId);
    if (!next) {
      throw Error(`Next element with id: ${elementId} not found`);
    }

    const replace = prev.hasAttribute(_HXREPLACE);
    if (replace) {
      const parent = prev.parentElement;
      if (!parent) {
        throw Error(`No parent found for element with id: ${elementId}`);
      }

      prev.outerHTML = next.outerHTML;
      this.#process(parent);
      this.#miniAlpine?.init(parent);
    } else {
      prev.innerHTML = next.innerHTML;
      this.#process(prev);
      this.#miniAlpine?.init(prev);
    }
  }

  // ---------------------------------------------------------------------------
  // Swap Strategies
  // ---------------------------------------------------------------------------

  /**
   * Prepares the strategy for a given element, setting loading states and dispatching before-swap events.
   * @param {Element} element - The element to prepare the strategy for.
   */
  #preStrategy(element) {
    element.dispatchEvent(new Event(_HXEVENTBEFORESWAP, { bubbles: true }));
  }

  /**
   * Prepares the strategy for a given element, setting loading states and dispatching before-swap events.
   * @param {Element} element - The element to prepare the strategy for.
   */
  #postStrategy(element) {
    let id = this.#getId(element);
    let target = document.getElementById(id);
    if (!target) {
      target = document.body;
    }
    target.dispatchEvent(new Event(_HXEVENTAFTERSWAP, { bubbles: true }));
  }

  /**
   * Delegates the strategy for a given element based on its hx-* attributes.
   * @param {Element} element - The element to delegate the strategy for.
   */
  async #delegateStrategy(element) {
    if (this.#loading.value) return; // Only one fetch at a time.
    this.#loading.value = true;

    this.#preStrategy(element);
    const silent = !element.hasAttribute(_HXSWAP);

    try {
      let url;

      switch (true) {
        case element.hasAttribute(_HXGETPAGE):
          url = this.#getUrl(element, _HXGETPAGE);
          await this.#strategyGetPage(element, url, silent);
          break;
        case element.hasAttribute(_HXGET):
          url = this.#getUrl(element, _HXGET);
          await this.#strategyGet(element, url, silent);
          break;
        case element.hasAttribute(_HXPOST):
          url = this.#getUrl(element, _HXPOST);
          await this.#strategyPost(element, url, silent);
          break;
      }
    } finally {
      this.#loading.value = false;
      this.#postStrategy(element);
    }
  }

  /**
   * Handles the GETPAGE strategy for a given element, including UI updates and fetching content.
   * @param {Element} element - The element to handle the GETPAGE strategy for.
   * @param {string} url - The URL to fetch content from.
   * @param {boolean} silent - Whether to update the UI with the fetched content.
   */
  async #strategyGetPage(element, url, silent) {
    const payload = this.#extractFetchPayload(element);
    const response = await this.#payloadGet(url, payload);
    if (!response.ok) {
      throw Error(`[MiniHtmx]: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get("Content-Type") ?? "";
    if (!contentType.includes("text/html")) {
      throw new Error(
        `[MiniHtmx]: Expected text/html but got "${contentType}"`,
      );
    }

    if (silent) return;

    let data = await response.text();
    const parsedHtml = new DOMParser().parseFromString(data, "text/html");
    const groups = this.#getSwapGroups(element);
    groups.forEach((group) => {
      const ids = this.#getElementIds(group);
      ids.forEach((id) => this.#swapContent(id, parsedHtml));
    });
  }

  /**
   * Handles the GET strategy for a given element, including UI updates and fetching content.
   * @param {Element} element - The element to handle the GET strategy for.
   * @param {string} url - The URL to fetch content from.
   * @param {boolean} silent - Whether to update the UI with the fetched content.
   */
  async #strategyGet(element, url, silent) {
    const payload = this.#extractFetchPayload(element);
    let sectionMap = null;
    if (!silent) {
      const { sectionIds, sectionMap: map } = this.#extractSectionIds(element);
      sectionMap = map;
      payload["sections"] = sectionIds;
    }

    const response = await this.#payloadGet(url, payload);
    if (!response.ok) {
      throw Error(`[MiniHtmx]: ${response.status} ${response.statusText}`);
    }

    if (silent) return;

    let sections = await response.json();
    if (!sections) throw Error(`[MiniHtmx]: No sections returned`);

    Object.entries(sections).forEach(([section, rawHtml]) => {
      const parsedHtml = new DOMParser().parseFromString(rawHtml, "text/html");
      const sectionId = /** @type {SectionId} */ (section);
      // Guarunteed to be non-null because no sections would be returned if it was.
      sectionMap?.[sectionId].forEach((id) => {
        this.#swapContent(id, parsedHtml);
      });
    });
  }

  /**
   * Handles the POST strategy for a given element, including UI updates and fetching content.
   * @param {Element} element - The element to handle the POST strategy for.
   * @param {string} url - The URL to fetch content from.
   * @param {boolean} silent - Whether to update the UI with the fetched content.
   */
  async #strategyPost(element, url, silent) {
    const payload = this.#extractFetchPayload(element);
    let sectionMap = null;
    if (!silent) {
      const { sectionIds, sectionMap: map } = this.#extractSectionIds(element);
      sectionMap = map;
      payload["sections"] = sectionIds;
    }

    const response = await this.#payloadPost(url, payload);
    if (!response.ok) {
      throw Error(`[MiniHtmx]: ${response.status} ${response.statusText}`);
    }

    if (silent) return;

    let data = await response.json();
    const sections = data?.sections ?? null;
    if (!sections) throw Error(`[MiniHtmx]: No sections returned`);

    Object.entries(sections).forEach(([secId, rawHtml]) => {
      const parsedHtml = new DOMParser().parseFromString(rawHtml, "text/html");
      const sectionId = /** @type {SectionId} */ (secId);
      // Guarunteed to be non-null because no sections would be returned if it was.
      sectionMap?.[sectionId].forEach((id) => {
        this.#swapContent(id, parsedHtml);
      });
    });
  }

  /**
   * Makes a POST request to the specified URL with the given payload.
   * @param {string} url - The URL to post to.
   * @param {object} payload - The payload to send.
   * @returns {Promise<Response>} - The response from the server.
   */
  #payloadPost(url, payload) {
    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });
  }

  /**
   * Makes a GET request to the specified URL with the given payload.
   * @param {string} endpoint - The URL to post to.
   * @param {Record<string, any>} payload - The payload to send.
   * @returns {Promise<Response>} - The response from the server.
   */
  #payloadGet(endpoint, payload) {
    const url = new URL(endpoint, window.location.origin);
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(payload)) {
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v));
      } else {
        params.append(key, value);
      }
    }

    let searchString = params.toString().replace(/=(?=&|$)/g, "=%22%22");
    url.search = searchString;
    return fetch(url, { method: "GET" });
  }

  // ---------------------------------------------------------------------------
  // Payload Parsing
  // ---------------------------------------------------------------------------

  /**
   * Extracts the fetch payload from the given element.
   * @param {Element} element - The element to extract the payload from.
   * @returns {Record<string, any>} - The extracted JSON payload.
   */
  #extractFetchPayload(element) {
    /**
     * Parses a bracket-notation key into path segments.
     * e.g. "properties[upload]" → ["properties", "upload"]
     *      "_cl-upload[]"       → ["_cl-upload", ""]
     * @param {string} key
     * @returns {string[]}
     */
    function parseKey(key) {
      const segments = [];
      const pattern = /([^\[\]]+)|\[([^\[\]]*)\]/g;
      let match;
      while ((match = pattern.exec(key)) !== null) {
        segments.push(match[1] !== undefined ? match[1] : match[2]);
      }
      return segments;
    }

    /**
     * Sets a value on a nested object using path segments.
     * Empty string segment ("") means "append to array".
     * @param {Record<string, any>} obj
     * @param {string[]} segments
     * @param {string} value
     */
    function setNested(obj, segments, value) {
      let current = obj;
      for (let i = 0; i < segments.length - 1; i++) {
        const seg = segments[i];
        const nextSeg = segments[i + 1];
        const nextIsArray = nextSeg === ""; // only explicit [] means array now

        if (!(seg in current)) {
          current[seg] = nextIsArray ? [] : {};
        }
        current = current[seg];
      }

      const last = segments[segments.length - 1];
      if (last === "") {
        if (!Array.isArray(current)) {
          throw new Error("Expected array for [] segment");
        }
        current.push(value);
      } else {
        // numeric-looking keys are just object keys now, not array indices
        current[last] = value;
      }
    }

    /** @type {Record<string, any>} */
    let payload = {};

    const formData = new FormData(/** @type {HTMLFormElement} */ (element));
    for (const [key, value] of formData.entries()) {
      const segments = parseKey(key);
      if (segments.length > 1) {
        setNested(payload, segments, value);
        continue;
      }

      if (!(key in payload)) {
        payload[key] = value;
      } else if (Array.isArray(payload[key])) {
        payload[key].push(value);
      } else {
        payload[key] = [payload[key], value];
      }
    }
    return payload;
  }

  // ---------------------------------------------------------------------------
  // Section Parsing
  // ---------------------------------------------------------------------------

  /**
   * @param {GroupId} groupID
   * @returns {ElementId[]}
   */
  #getElementIds(groupID) {
    const result = document.body
      .querySelectorAll(`[${_HXGROUPID}~="${groupID}"]`)
      .map((element) => element.getAttribute("id"))
      .filter((id) => id != null);
    if (result.length === 0) {
      throw Error(`[MiniHtmx]: No element ids found for group "${groupID}"`);
    }
    return /** @type {ElementId[]} */ (result);
  }

  /**
   * Extracts the section IDs from the given element and its parents.
   * @param {Element} element - The element to extract section IDs from.
   * @returns {{ sectionIds: SectionId[]; sectionMap: Record<SectionId, ElementId[]> }} - The section IDs that the changes target.
   */
  #extractSectionIds(element) {
    /**
     * @param {string} id
     * @returns {GroupId}
     */
    function asGroupID(id) {
      return /** @type {GroupId} */ (id);
    }

    const groups = this.#getSwapGroups(element);
    const sectionMap = groups.reduce((acc, rawID) => {
      const groupID = asGroupID(rawID.trim());
      const elementIds = this.#getElementIds(groupID);
      for (const id of elementIds) {
        const sectionId = this.#getSectionId(id);
        const current = acc[sectionId] || [];
        acc[sectionId] = [...current, id];
      }
      return acc;
    }, /**@type {Record<SectionId, ElementId[]>} */ ({}));

    const sectionIds = /** @type {SectionId[]} */ (Object.keys(sectionMap));
    if (sectionIds.length === 0) {
      const id = this.#getId(element);
      throw Error(`[MiniHtmx]: No section IDs found for element #${id}`);
    }

    return { sectionIds, sectionMap };
  }

  /**
   * Returns the section ID for the given element, including any parent sections.
   * @param {ElementId} id - The element ID to look up.
   * @returns {SectionId} - The section ID of the element.
   */
  #getSectionId(id) {
    /**
     * @param {string} id
     * @returns {SectionId}
     */
    function _cast(id) {
      return /** @type {SectionId} */ (id);
    }

    const element = document.getElementById(id);
    if (!element) throw Error(`[MiniHtmx]: "#${id}" not found`);
    const elementSectionId = element.getAttribute(_HXSECTIONID);
    if (elementSectionId) return _cast(elementSectionId);

    const parentSectionId = element
      .closest(`[${_HXSECTIONID}]`)
      ?.getAttribute(_HXSECTIONID);
    if (parentSectionId) return _cast(parentSectionId);

    const section = element.closest('[id*="shopify-section-"]');
    if (!section) {
      throw Error(`[MiniHtmx]: No parent has a section id found for "${id}"`);
    }

    const sectionId = section.getAttribute("id");
    if (!sectionId) {
      throw Error(
        `[MiniHtmx]: "runtime error, section id should exist for element"`,
      );
    }
    const justId = sectionId.replace(/^shopify-section-/, "");
    return _cast(justId);
  }

  /**
   * Handles element-specific trigger logic
   * @param {Element} element
   * @returns {string}
   */
  #getDefaultTrigger(element) {
    const tag = element.tagName.toUpperCase();
    if (tag === "FORM") return "submit";
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT")
      return "change";
    return "click";
  }

  // ---------------------------------------------------------------------------
  // Utils
  // ---------------------------------------------------------------------------

  /**
   *
   * @param {Element} element
   * @returns {GroupId[]}
   */
  #getSwapGroups(element) {
    const groups = element
      .getAttribute(_HXSWAP)
      ?.split(" ")
      .map((g) => g.trim());
    if (!groups || groups.length === 0) {
      throw new Error(
        `[MiniHtmx]: No swap groups specified for element #"${element.id}"`,
      );
    }
    return /** @type {GroupId[]} */ (groups);
  }

  /**
   * Returns the id of the element, throwing an error if no id is specified.
   * @param {Element} element
   * @returns {ElementId}
   */
  #getId(element) {
    const id = element.getAttribute("id");
    if (!id) {
      throw new Error(
        `[MiniHtmx]: No id specified for element ${element.outerHTML}`,
      );
    }
    return /** @type {ElementId} */ (id);
  }

  /**
   * Retrieves the URL for a given element and attribute.
   * @param {Element} element - The element to retrieve the URL for.
   * @param {string} attribute - The attribute to retrieve the URL from.
   * @returns {string} The URL for the given element and attribute.
   */
  #getUrl(element, attribute) {
    const url = element.getAttribute(attribute);
    if (!url) {
      const id = this.#getId(element);
      throw Error(`No URL specified for ${_HXGETPAGE} on element #${id}`);
    }
    return url;
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  constructor() {
    this.#loading = signal(false);
    this.#preloading = signal(false);
  }

  /**
   * Initialises MyHtmx on DOMContentLoaded.
   * Scans the full document, sets up a MutationObserver for dynamically added nodes
   */
  init() {
    this.#process();

    // @ts-ignore
    this.#miniAlpine = new MiniAlpine(this);
    this.#miniAlpine?.init();

    this.#observer = new MutationObserver((mutations) => {
      mutations.forEach(({ addedNodes }) => {
        addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.#process(/** @type {Element} */ (node));
            this.#miniAlpine?.init(/** @type {HTMLElement} */ (node));
          }
        });
      });
    });
    this.#observer.observe(document.body, { childList: true, subtree: true });
  }

  destroy() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#miniAlpine?.dispose(document.body);
    this.#cleanup(document.body);
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const miniHtmx = new MiniHTMX();
  miniHtmx.init();
});


/* --- js/critical/htmlelement-ext.js --- */
Object.defineProperty(HTMLElement.prototype, "throw", {
  get() {
    const self = this;
    return {
      /**
       * Returns all elements with the given query, or throws an error if none exist.
       * @param {string} query - CSS selector string
       * @returns {NodeListOf<HTMLElement>}
       * @throws {Error} When no elements match the query
       */
      any(query) {
        const elements = self.querySelectorAll(query);
        if (elements.length === 0) {
          throw new Error(`No elements found matching query: ${query}`);
        }
        return elements;
      },

      /**
       * Returns the element with the given id, or throws an error if it does not exist.
       * @param {string} id - Element ID
       * @returns {HTMLElement}
       * @throws {Error} When no element matches the id
       */
      byId(id) {
        const element = document.getElementById(id);
        if (!element) {
          throw new Error(`No element found with id: ${id}`);
        }
        return element;
      },

      /**
       * Returns the element with the given query, or throws an error if it does not exist.
       * @param {string} query - CSS selector string
       * @returns {HTMLElement}
       * @throws {Error} When no element matches the query
       */
      element(query) {
        const element = self.querySelector(query);
        if (!element) {
          throw new Error(`No element found matching query: ${query}`);
        }
        return element;
      },
    };
  },
});


/* --- js/critical/signals.js --- */
/**
 * @fileoverview Core reactivity system (Signal, Computed, Effect).
 */

// --- Flags & Globals ---
const RUNNING = 1 << 0;
const NOTIFIED = 1 << 1;
const OUTDATED = 1 << 2;
const DISPOSED = 1 << 3;
const HAS_ERROR = 1 << 4;
const TRACKING = 1 << 5;

/** @type {Computed<any> | Effect | undefined} */
let evalContext = undefined;

/** @type {Effect | undefined} */
let batchedEffect = undefined;

/** @type {number} */
let batchDepth = 0;

/** @type {number} */
let batchIteration = 0;

/** @type {number} */
let globalVersion = 0;

// --- Batching & Cycle Detection ---

/** @internal */
function startBatch() {
  batchDepth++;
}

/** @internal */
function endBatch() {
  if (batchDepth > 1) {
    batchDepth--;
    return;
  }

  /** @type {unknown} */
  let error;
  let hasError = false;

  while (batchedEffect !== undefined) {
    let effect = /** @type {Effect | undefined} */ (batchedEffect);
    batchedEffect = undefined;

    batchIteration++;

    while (effect !== undefined) {
      const next = effect._nextBatchedEffect;
      effect._nextBatchedEffect = undefined;
      effect._flags &= ~NOTIFIED;

      if (!(effect._flags & DISPOSED) && needsToRecompute(effect)) {
        try {
          effect._callback();
        } catch (err) {
          if (!hasError) {
            error = err;
            hasError = true;
          }
        }
      }
      effect = next;
    }
  }

  batchIteration = 0;
  batchDepth--;

  if (hasError) throw error;
}

/**
 * Batches multiple signal updates into a single flush of effects.
 * @template T
 * @param {() => T} fn The callback containing state mutations.
 * @returns {T} The result of the callback.
 */
function batch(fn) {
  if (batchDepth > 0) return fn();
  startBatch();
  try {
    return fn();
  } finally {
    endBatch();
  }
}

// --- Doubly-Linked List Dependency Tracking ---

/**
 * A linked list node used to track dependencies (sources) and dependents (targets).
 * @typedef {Object} Node
 * @property {Signal<any>} _source A source whose value the target depends on.
 * @property {Node} [_prevSource]
 * @property {Node} [_nextSource]
 * @property {Computed<any> | Effect} _target A target that depends on the source.
 * @property {Node} [_prevTarget]
 * @property {Node} [_nextTarget]
 * @property {number} _version The version of the source the target last saw.
 * @property {Node} [_rollbackNode] Used to restore the node state context.
 */

/**
 * @internal
 * @param {Signal<any>} signal
 * @returns {Node | undefined}
 */
function addDependency(signal) {
  if (evalContext === undefined) return undefined;

  let node = signal._node;
  if (node === undefined || node._target !== evalContext) {
    node = {
      _version: 0,
      _source: signal,
      _prevSource: evalContext._sources,
      _nextSource: undefined,
      _target: evalContext,
      _prevTarget: undefined,
      _nextTarget: undefined,
      _rollbackNode: node,
    };

    if (evalContext._sources !== undefined) {
      evalContext._sources._nextSource = node;
    }
    evalContext._sources = node;
    signal._node = node;

    if (evalContext._flags & TRACKING) {
      signal._subscribe(node);
    }
    return node;
  } else if (node._version === -1) {
    node._version = 0;
    if (node._nextSource !== undefined) {
      node._nextSource._prevSource = node._prevSource;
      if (node._prevSource !== undefined) {
        node._prevSource._nextSource = node._nextSource;
      }
      node._prevSource = evalContext._sources;
      node._nextSource = undefined;
      if (!evalContext._sources) {
        throw Error('evalContext._sources is undefined');
      }
      evalContext._sources._nextSource = node;
      evalContext._sources = node;
    }
    return node;
  }
  return undefined;
}

// --- Core Signal ---

/**
 * The base class for plain and computed signals.
 * @template T
 */
class Signal {
  /**
   * @param {T} [value]
   */
  constructor(value) {
    /** @type {T | undefined} @internal */
    this._value = value;
    /** @type {number} @internal */
    this._version = 0;
    /** @type {Node | undefined} @internal */
    this._node = undefined;
    /** @type {Node | undefined} @internal */
    this._targets = undefined;
  }

  /**
   * @internal
   * @returns {boolean}
   */
  _refresh() {
    return true;
  }

  /**
   * @internal
   * @param {Node} node
   */
  _subscribe(node) {
    const targets = this._targets;
    if (targets !== node && node._prevTarget === undefined) {
      node._nextTarget = targets;
      this._targets = node;
      if (targets !== undefined) {
        targets._prevTarget = node;
      }
    }
  }

  /**
   * @internal
   * @param {Node} node
   */
  _unsubscribe(node) {
    if (this._targets !== undefined) {
      const prev = node._prevTarget;
      const next = node._nextTarget;
      if (prev !== undefined) {
        prev._nextTarget = next;
        node._prevTarget = undefined;
      }
      if (next !== undefined) {
        next._prevTarget = prev;
        node._nextTarget = undefined;
      }
      if (node === this._targets) {
        this._targets = next;
      }
    }
  }

  /**
   * Reads the current value without registering a dependency.
   * @returns {T}
   */
  peek() {
    return /** @type {T} */ (this._value);
  }

  /**
   * @returns {T}
   */
  get value() {
    const node = addDependency(this);
    if (node !== undefined) node._version = this._version;
    return /** @type {T} */ (this._value);
  }

  /**
   * @param {T} value
   */
  set value(value) {
    if (value !== this._value) {
      if (batchIteration > 100) throw new Error('Cycle detected');

      this._value = value;
      this._version++;
      globalVersion++;

      startBatch();
      try {
        for (
          let node = this._targets;
          node !== undefined;
          node = node._nextTarget
        ) {
          node._target._notify();
        }
      } finally {
        endBatch();
      }
    }
  }
}

/**
 * Creates a new plain signal.
 * @template T
 * @param {T} value The initial value.
 * @returns {Signal<T>}
 */
function signal(value) {
  return new Signal(value);
}

// --- Lazy Evaluation Helpers ---

/**
 * @internal
 * @param {Computed<any> | Effect} target
 * @returns {boolean}
 */
function needsToRecompute(target) {
  for (
    let node = target._sources;
    node !== undefined;
    node = node._nextSource
  ) {
    if (
      node._source._version !== node._version ||
      !node._source._refresh() ||
      node._source._version !== node._version
    ) {
      return true;
    }
  }
  return false;
}

/**
 * @internal
 * @param {Computed<any> | Effect} target
 */
function prepareSources(target) {
  for (
    let node = target._sources;
    node !== undefined;
    node = node._nextSource
  ) {
    const rollbackNode = node._source._node;
    if (rollbackNode !== undefined) node._rollbackNode = rollbackNode;
    node._source._node = node;
    node._version = -1;
    if (node._nextSource === undefined) {
      target._sources = node;
      break;
    }
  }
}

/**
 * @internal
 * @param {Computed<any> | Effect} target
 */
function cleanupSources(target) {
  let node = target._sources;
  let head = undefined;

  while (node !== undefined) {
    const prev = node._prevSource;
    if (node._version === -1) {
      node._source._unsubscribe(node);
      if (prev !== undefined) prev._nextSource = node._nextSource;
      if (node._nextSource !== undefined) node._nextSource._prevSource = prev;
    } else {
      head = node;
    }
    node._source._node = node._rollbackNode;
    if (node._rollbackNode !== undefined) node._rollbackNode = undefined;
    node = prev;
  }
  target._sources = head;
}

// --- Computed ---

/**
 * Read-only signal derived from other signals.
 * @template T
 * @extends {Signal<T>}
 */
class Computed extends Signal {
  /**
   * @param {() => T} fn
   */
  constructor(fn) {
    super(undefined);
    /** @type {() => T} @internal */
    this._fn = fn;
    /** @type {Node | undefined} @internal */
    this._sources = undefined;
    /** @type {number} @internal */
    this._globalVersion = globalVersion - 1;
    /** @type {number} @internal */
    this._flags = OUTDATED;
  }

  /**
   * @internal
   * @override
   * @returns {boolean}
   */
  _refresh() {
    this._flags &= ~NOTIFIED;
    if (this._flags & RUNNING) return false;

    if ((this._flags & (OUTDATED | TRACKING)) === TRACKING) return true;
    this._flags &= ~OUTDATED;

    if (this._globalVersion === globalVersion) return true;
    this._globalVersion = globalVersion;

    this._flags |= RUNNING;
    if (this._version > 0 && !needsToRecompute(this)) {
      this._flags &= ~RUNNING;
      return true;
    }

    const prevContext = evalContext;
    try {
      prepareSources(this);
      evalContext = this;
      const value = this._fn();
      if (
        this._flags & HAS_ERROR ||
        this._value !== value ||
        this._version === 0
      ) {
        this._value = value;
        this._flags &= ~HAS_ERROR;
        this._version++;
      }
    } catch (err) {
      this._value = /** @type {any} */ (err);
      this._flags |= HAS_ERROR;
      this._version++;
    }

    evalContext = prevContext;
    cleanupSources(this);
    this._flags &= ~RUNNING;
    return true;
  }

  /**
   * @internal
   * @override
   * @param {Node} node
   */
  _subscribe(node) {
    if (this._targets === undefined) {
      this._flags |= OUTDATED | TRACKING;
      for (let n = this._sources; n !== undefined; n = n._nextSource) {
        n._source._subscribe(n);
      }
    }
    super._subscribe(node);
  }

  /**
   * @internal
   * @override
   * @param {Node} node
   */
  _unsubscribe(node) {
    if (this._targets !== undefined) {
      super._unsubscribe(node);
      if (this._targets === undefined) {
        this._flags &= ~TRACKING;
        for (let n = this._sources; n !== undefined; n = n._nextSource) {
          n._source._unsubscribe(n);
        }
      }
    }
  }

  /**
   * @internal
   */
  _notify() {
    if (!(this._flags & NOTIFIED)) {
      this._flags |= OUTDATED | NOTIFIED;
      for (
        let node = this._targets;
        node !== undefined;
        node = node._nextTarget
      ) {
        node._target._notify();
      }
    }
  }

  /**
   * @returns {T}
   */
  get value() {
    if (this._flags & RUNNING) throw new Error('Cycle detected');

    const node = addDependency(this);
    this._refresh();

    if (node !== undefined) node._version = this._version;
    if (this._flags & HAS_ERROR) throw this._value;
    return /** @type {T} */ (this._value);
  }
}

/**
 * Creates a read-only signal computed based on the values of other signals.
 * @template T
 * @param {() => T} fn
 * @returns {Omit<Signal<T>, "value"> & { readonly value: T }}
 */
function computed(fn) {
  return new Computed(fn);
}

// --- Effect ---

/**
 * @callback CleanupFn
 * @returns {void}
 */

/**
 * @callback EffectFn
 * @returns {void | CleanupFn}
 */

class Effect {
  /**
   * @param {EffectFn} fn
   */
  constructor(fn) {
    /** @type {EffectFn | undefined} @internal */
    this._fn = fn;
    /** @type {CleanupFn | undefined} @internal */
    this._cleanup = undefined;
    /** @type {Node | undefined} @internal */
    this._sources = undefined;
    /** @type {Effect | undefined} @internal */
    this._nextBatchedEffect = undefined;
    /** @type {number} @internal */
    this._flags = TRACKING;
  }

  /**
   * @internal
   */
  _callback() {
    const finish = this._start();
    try {
      if (this._flags & DISPOSED) return;
      if (this._fn === undefined) return;
      const cleanup = this._fn();
      if (typeof cleanup === 'function') this._cleanup = cleanup;
    } finally {
      finish();
    }
  }

  /**
   * @internal
   * @returns {() => void}
   */
  _start() {
    if (this._flags & RUNNING) throw new Error('Cycle detected');
    this._flags |= RUNNING;
    this._flags &= ~DISPOSED;

    this._disposeCleanup();
    prepareSources(this);

    startBatch();
    const prevContext = evalContext;
    evalContext = this;

    return () => {
      if (evalContext !== this) throw new Error('Out-of-order effect');
      cleanupSources(this);
      evalContext = prevContext;
      this._flags &= ~RUNNING;
      if (this._flags & DISPOSED) this._dispose();
      endBatch();
    };
  }

  /**
   * @internal
   */
  _notify() {
    if (!(this._flags & NOTIFIED)) {
      this._flags |= NOTIFIED;
      this._nextBatchedEffect = batchedEffect;
      batchedEffect = this;
    }
  }

  /**
   * @internal
   */
  _disposeCleanup() {
    const cleanup = this._cleanup;
    this._cleanup = undefined;
    if (typeof cleanup === 'function') {
      startBatch();
      const prevContext = evalContext;
      evalContext = undefined;
      try {
        cleanup();
      } finally {
        evalContext = prevContext;
        endBatch();
      }
    }
  }

  /**
   * @internal
   */
  _dispose() {
    this._flags |= DISPOSED;
    if (!(this._flags & RUNNING)) {
      for (
        let node = this._sources;
        node !== undefined;
        node = node._nextSource
      ) {
        node._source._unsubscribe(node);
      }
      this._fn = undefined;
      this._sources = undefined;
      this._disposeCleanup();
    }
  }
}

/**
 * Runs a side-effect that tracks dependencies and re-runs when they change.
 * @param {EffectFn} fn
 * @returns {() => void} A disposal function to tear down the effect.
 */
function effect(fn) {
  const e = new Effect(fn);
  try {
    e._callback();
  } catch (err) {
    e._dispose();
    throw err;
  }
  return e._dispose.bind(e);
}


/* --- js/critical/utils.js --- */
/**
 * Debounces a function to limit how often it can be called.
 *
 * @template {(...args: any[]) => void} T
 * @param {T} fn
 * @param {number} delay
 * @returns {T}
 */
window.debounce = function (fn, delay) {
  let /** @type {ReturnType<typeof setTimeout>} */ timeout;
  return /** @type {T} */ (
    (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), delay);
    }
  );
};

/**
 * Unwraps a nullable value, throwing if null/undefined.
 * @template T
 * @param {T | null | undefined} value
 * @param {string} [message]
 * @returns {T}
 */
window.unwrap = function (value, message) {
  if (value == null) throw new Error(message || 'Value is null or undefined');
  return value;
};

/**
 * Converts a string of numbers into a currency formatted string.
 * @param {string} value
 * @returns {string}
 */
window.money = function (value) {
  // Convert string inputs to a float, fallback to 0 if invalid
  let number = parseFloat(value);
  if (isNaN(number)) {
    throw Error(`${value} is not a number`);
  }

  number = number / 100;

  return `\$${number.toFixed(2)}`;
};
